import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyService } from '@/services/survey.service';
import { SurveyContext, SubmitSurveyPayload } from '@/types/survey';
import SurveyLoadingPage from './SurveyLoadingPage';

// ---- Types ----
type FormData = Required<SubmitSurveyPayload>;
type StepErrors = Partial<Record<keyof FormData, string>>;

// ---- Step config ----
interface FieldConfig {
  key: keyof FormData;
  label: string;
  type: 'select' | 'textarea';
  options?: string[];
  required: boolean;
}

interface StepConfig {
  title: string;
  fields: FieldConfig[];
}

const STEPS: StepConfig[] = [
  {
    title: 'Demographics',
    fields: [
      {
        key: 'age',
        label: 'Age Range',
        type: 'select',
        options: [
          'Under 18',
          '18-25',
          '26-35',
          '36-45',
          '46-55',
          '56-65',
          '66+',
        ],
        required: true,
      },
      {
        key: 'gender',
        label: 'Gender',
        type: 'select',
        options: ['Male', 'Female', 'Non-binary', 'Prefer not to say'],
        required: true,
      },
      {
        key: 'handedness',
        label: 'Handedness',
        type: 'select',
        options: ['Right-handed', 'Left-handed'],
        required: true,
      },
    ],
  },
  {
    title: 'Golf Background',
    fields: [
      {
        key: 'handicap',
        label: 'Current Handicap',
        type: 'select',
        options: [
          'Scratch (0)',
          '1-5',
          '6-10',
          '11-15',
          '16-20',
          '21-25',
          '26+',
        ],
        required: true,
      },
      {
        key: 'playingDuration',
        label: 'How long have you been playing?',
        type: 'select',
        options: [
          'Less than 1 year',
          '1-3 years',
          '4-7 years',
          '8-15 years',
          'More than 15 years',
        ],
        required: true,
      },
      {
        key: 'playFrequency',
        label: 'How often do you play?',
        type: 'select',
        options: [
          'Rarely (a few times a year)',
          'Monthly',
          '2-3 times per month',
          'Weekly',
          'Multiple times per week',
        ],
        required: true,
      },
    ],
  },
  {
    title: 'Fitness & Equipment',
    fields: [
      {
        key: 'practiceFrequency',
        label: 'How often do you practice?',
        type: 'select',
        options: [
          'Never',
          'Rarely',
          'Monthly',
          'Weekly',
          'Multiple times per week',
        ],
        required: true,
      },
      {
        key: 'gymExperience',
        label: 'Gym / Fitness Experience',
        type: 'select',
        options: ['None', 'Beginner', 'Intermediate', 'Advanced'],
        required: true,
      },
      {
        key: 'clubConfidence',
        label: 'Confidence with your current clubs',
        type: 'select',
        options: [
          'Not confident at all',
          'Somewhat confident',
          'Confident',
          'Very confident',
        ],
        required: true,
      },
      {
        key: 'lastFitting',
        label: 'Last club fitting',
        type: 'select',
        options: [
          'Never',
          'More than 3 years ago',
          '1-3 years ago',
          'Within the last year',
        ],
        required: true,
      },
    ],
  },
  {
    title: 'Goals & Commitment',
    fields: [
      {
        key: 'golfGoals',
        label: 'What are your golf goals?',
        type: 'textarea',
        required: true,
      },
      {
        key: 'commitmentLevel',
        label: 'Commitment Level (1-10)',
        type: 'select',
        options: [
          '1 - Low commitment',
          '2',
          '3',
          '4',
          '5 - Moderate commitment',
          '6',
          '7',
          '8',
          '9',
          '10 - Fully committed',
        ],
        required: true,
      },
    ],
  },
];

const EMPTY_FORM: FormData = {
  age: '',
  gender: '',
  handedness: '',
  handicap: '',
  playingDuration: '',
  playFrequency: '',
  practiceFrequency: '',
  gymExperience: '',
  clubConfidence: '',
  lastFitting: '',
  golfGoals: '',
  commitmentLevel: 0,
};

function initFormData(context: SurveyContext): FormData {
  if (!context.active) return { ...EMPTY_FORM };
  return {
    age: context.active.age ?? '',
    gender: context.active.gender ?? '',
    handedness: context.active.handedness ?? '',
    handicap: context.active.handicap ?? '',
    playingDuration: context.active.playingDuration ?? '',
    playFrequency: context.active.playFrequency ?? '',
    practiceFrequency: context.active.practiceFrequency ?? '',
    gymExperience: context.active.gymExperience ?? '',
    clubConfidence: context.active.clubConfidence ?? '',
    lastFitting: context.active.lastFitting ?? '',
    golfGoals: context.active.golfGoals ?? '',
    commitmentLevel: context.active.commitmentLevel ?? 0,
  };
}

function validateStep(step: StepConfig, formData: FormData): StepErrors {
  const errors: StepErrors = {};
  for (const field of step.fields) {
    if (!field.required) continue;
    const value = formData[field.key];
    if (field.key === 'commitmentLevel') {
      if (typeof value !== 'number' || (value as number) < 1) {
        errors[field.key] = 'This field is required';
      }
    } else {
      if (typeof value !== 'string' || value.trim() === '') {
        errors[field.key as keyof StepErrors] = 'This field is required';
      }
    }
  }
  return errors;
}

// Map commitment level option string to its numeric value (1-indexed)
function commitmentOptionToNumber(option: string): number {
  const match = option.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

export default function SurveyFormPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<SurveyContext | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({ ...EMPTY_FORM });
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/survey/error', { replace: true });
      return;
    }
    surveyService
      .getContext(token)
      .then((ctx) => {
        setContext(ctx);
        setFormData(initFormData(ctx));
        setLoading(false);
      })
      .catch(() => {
        navigate('/survey/error', { replace: true });
      });
  }, [token, navigate]);

  if (loading) return <SurveyLoadingPage />;
  if (!context) return null;

  const firstName = context.memberName.trim().split(/\s+/)[0];
  const step = STEPS[currentStep];

  function handleFieldChange(key: keyof FormData, value: string) {
    setFormData((prev) => {
      if (key === 'commitmentLevel') {
        return { ...prev, [key]: commitmentOptionToNumber(value) };
      }
      return { ...prev, [key]: value };
    });
    // Clear error for the changed field
    if (errors[key as keyof StepErrors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as keyof StepErrors];
        return next;
      });
    }
  }

  function handleNext() {
    const stepErrors = validateStep(step, formData);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep((s) => s + 1);
  }

  function handleBack() {
    setErrors({});
    setCurrentStep((s) => s - 1);
  }

  async function handleSubmit() {
    const stepErrors = validateStep(step, formData);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setSubmitError(null);
    try {
      await surveyService.submit(token!, formData);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to submit survey'
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Thank-you screen
  if (submitted) {
    return (
      <div className="max-w-screen-sm mx-auto p-6 pt-12 text-center">
        <div className="text-4xl mb-4">&#10003;</div>
        <h1 className="text-xl font-semibold mb-3">Thank you, {firstName}!</h1>
        <p className="text-sm text-gray-700">
          Your survey has been submitted. Your golf professional will review
          your responses and use them to personalize your experience at Tee Box.
        </p>
      </div>
    );
  }

  // Get the current value for a commitmentLevel field as a string for the select
  function commitmentDisplayValue(numVal: number): string {
    if (!numVal || numVal < 1) return '';
    const option = STEPS[3].fields[1].options?.find(
      (opt) => commitmentOptionToNumber(opt) === numVal
    );
    return option ?? '';
  }

  return (
    <div className="max-w-screen-sm mx-auto p-4 pt-8">
      {/* Greeting */}
      <h2 className="text-2xl font-semibold mb-1">Hi {firstName},</h2>

      {/* Step progress indicator */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm text-gray-600">
          Step {currentStep + 1} of {STEPS.length}
        </span>
        <div className="flex items-center gap-1 ml-2">
          {STEPS.map((_, idx) => (
            <span
              key={idx}
              className={
                idx === currentStep
                  ? 'bg-[#bd9734] rounded-full w-2 h-2 inline-block'
                  : 'bg-gray-300 rounded-full w-2 h-2 inline-block'
              }
            />
          ))}
        </div>
      </div>

      {/* Step title */}
      <h3 className="text-lg font-medium mb-4">{step.title}</h3>

      {/* Fields */}
      {step.fields.map((field) => {
        const errorMsg = errors[field.key as keyof StepErrors];

        if (field.type === 'textarea') {
          return (
            <div key={field.key} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <textarea
                rows={4}
                placeholder="Describe your golf goals..."
                value={(formData[field.key] as string) ?? ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none focus:outline-none focus:border-[#bd9734]"
              />
              {errorMsg && (
                <p className="text-red-500 text-xs mt-1">{errorMsg}</p>
              )}
            </div>
          );
        }

        // Select field
        const selectValue =
          field.key === 'commitmentLevel'
            ? commitmentDisplayValue(formData.commitmentLevel as number)
            : (formData[field.key] as string) ?? '';

        return (
          <div key={field.key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <select
              value={selectValue}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm bg-white focus:outline-none focus:border-[#bd9734]"
            >
              <option value="" disabled>
                Select an option
              </option>
              {field.options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errorMsg && (
              <p className="text-red-500 text-xs mt-1">{errorMsg}</p>
            )}
          </div>
        );
      })}

      {/* Navigation buttons */}
      <div className="flex gap-3 mt-6">
        {currentStep > 0 && (
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 border border-gray-300 text-gray-700 rounded-lg py-3 text-sm font-medium"
          >
            Back
          </button>
        )}

        {currentStep < STEPS.length - 1 && (
          <button
            type="button"
            onClick={handleNext}
            className="flex-1 bg-[#bd9734] text-white rounded-lg py-3 text-sm font-medium"
          >
            Next
          </button>
        )}

        {currentStep === STEPS.length - 1 && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-[#bd9734] text-white rounded-lg py-3 text-sm font-medium disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        )}
      </div>

      {submitError && (
        <p className="text-red-500 text-sm mt-3 text-center">{submitError}</p>
      )}
    </div>
  );
}
